import React, { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';

// India has no daylight saving, so a fixed offset is exact. Mirrors backend/app/utils/schedule.py.
const IST_OFFSET_MIN = 330;
const MIN_LEAD_MS = 2 * 60 * 1000;
export const MAX_SCHEDULE_DAYS = 90;

/** Date for "N days from today (IST) at HH:MM IST", or null if the inputs are invalid. */
export function computeScheduledDate(days, timeStr, now = new Date()) {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeStr || '');
  const n = Number(days);
  if (!m || !Number.isInteger(n) || n < 0 || n > MAX_SCHEDULE_DAYS) return null;
  const istNow = new Date(now.getTime() + IST_OFFSET_MIN * 60000);
  const utcMs = Date.UTC(
    istNow.getUTCFullYear(),
    istNow.getUTCMonth(),
    istNow.getUTCDate() + n,
    Number(m[1]),
    Number(m[2]),
  ) - IST_OFFSET_MIN * 60000;
  return new Date(utcMs);
}

export function isSchedulable(days, timeStr, now = new Date()) {
  const target = computeScheduledDate(days, timeStr, now);
  return !!target && target.getTime() - now.getTime() >= MIN_LEAD_MS;
}

export function formatIst(date) {
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relative(date, now) {
  const mins = Math.round((date.getTime() - now.getTime()) / 60000);
  if (mins < 60) return `in ${Math.max(mins, 0)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  return `in ${Math.round(hours / 24)} days`;
}

/**
 * Days + time inputs with a live "sends on ..." line. Controlled: `days` and `time` come from
 * the parent. The send time is recalculated every 30 s so a stale "today" cannot slip through.
 */
export default function SchedulePicker({ days, time, onChange, disabled = false }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const target = computeScheduledDate(days, time, now);
  const valid = isSchedulable(days, time, now);

  return (
    <div className="schedule-picker">
      <div className="schedule-picker-inputs">
        <label>
          Send after (days)
          <input
            type="number"
            min={0}
            max={MAX_SCHEDULE_DAYS}
            value={days}
            disabled={disabled}
            onChange={(e) => onChange({ days: e.target.value === '' ? '' : Number(e.target.value), time })}
          />
        </label>
        <label>
          At (IST)
          <input
            type="time"
            value={time}
            disabled={disabled}
            onChange={(e) => onChange({ days, time: e.target.value })}
          />
        </label>
      </div>
      <p className={`schedule-picker-result${valid ? '' : ' invalid'}`}>
        <CalendarClock size={16} />
        {!target && 'Enter a number of days (0–90) and a time.'}
        {target && valid && (
          <span>
            Will be sent on <strong>{formatIst(target)} IST</strong> ({relative(target, now)}).
            <small> Starts within about 5 minutes of that time.</small>
          </span>
        )}
        {target && !valid && 'That time has already passed — pick a later time or more days.'}
      </p>
    </div>
  );
}
